import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { isNullOrUndefined } from 'util';
import { environment } from 'src/environments/environment';
import { GURUGRAM_DISTRICT_UUID } from './urban-heat-island-map/urban-heat-island-map-constants';

@Injectable({
  providedIn: 'root'
})
export class UrbanHeatIslandDashboardService {
  urlConst: any = environment.url.HEATWAVE_STAGING_API_URL;

  // Fetched exactly once per location context (district/ward change) and cached here. The
  // timeline play bar switches frames purely by re-reading this cache and flipping
  // activeTimelineDate$ — no additional API calls happen while animating/dragging the slider.
  correlationCache$: BehaviorSubject<{ response: any, payload: any, timelineDates: string[] }> = new BehaviorSubject(null);
  activeTimelineDate$: BehaviorSubject<string> = new BehaviorSubject(null);

  // Utilities distribution (buildings/schools/etc. bucketed by LST range), cached the same way as
  // correlationCache$ above — fetched once per location context; the category dropdown and the
  // shared timeline play bar both re-derive the chart from this single-slot cache with zero
  // additional API calls.
  utilitiesCache$: BehaviorSubject<{ response: any, payload: any }> = new BehaviorSubject(null);

  // "Layers" dropdown selection (UHI_OVERLAY_LAYERS), Gurgaon or ward level, published by
  // UrbanHeatIslandMapComponent.onOverlayToggle so the infobox's Utilities Exposure chart can
  // auto-select the matching category (see UHI_OVERLAY_TO_UTILITY_KEY) with no direct
  // map component -> infobox component binding.
  activeOverlayId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(private http: HttpClient, private datePipe: DatePipe) { }

  setActiveOverlayId(overlayId: string) {
    this.activeOverlayId$.next(overlayId);
  }

  // Called on fresh entry into the UHI module (UrbanHeatIslandMapComponent.ngAfterViewInit) —
  // this service is providedIn: 'root', so activeOverlayId$/utilitiesCache$ otherwise survive
  // component destroy/recreate across module navigation, unlike this.activeOverlayId (a plain
  // field on the map component, already fresh on the next instantiation) or selectedUtilityKey
  // (already reset by the infobox on every location-params change). Only clears the ward-level
  // "Layers" overlay selection + its cached chart data — correlationCache$/activeTimelineDate$
  // (location/date/drill-down state) are untouched, they already follow their own behavior.
  resetUtilityLayerState() {
    this.activeOverlayId$.next(null);
    this.utilitiesCache$.next(null);
  }

  getAggregatedCorrelationData(postData: any) {
    return this.http.post<any>(this.urlConst + 'correlation/getAggregatedCorrelationData', postData);
  }

  getCorrelationDateRange() {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 1);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 30);
    return {
      startTs: this.datePipe.transform(startDate, 'yyyyMMdd'),
      endTs: this.datePipe.transform(endDate, 'yyyyMMdd')
    };
  }

  buildCorrelationPayload(queryParams: any) {
    const { startTs, endTs } = this.getCorrelationDateRange();
    const isWardLevel = queryParams['locType'] === 'WARD';
    return {
      pEUUID: GURUGRAM_DISTRICT_UUID,
      pUUID: isWardLevel ? queryParams['pUUID'] : GURUGRAM_DISTRICT_UUID,
      pType: isWardLevel ? 'WARD' : 'DISTRICT',
      cType: 'WARD',
      // startTs,
      // endTs,
      "startTs":"20260322",
      "endTs": "20260525",
      format: 'DAILY',
      source: 'CORRELATION'
    };
  }

  // Fetches the correlation API once for the given location context, derives the available
  // timeline dates, and defaults the active frame to the latest one. Called only when the
  // location context changes (district/ward drilldown, breadcrumb, search) — never on a frame
  // change. Consumers (map service, infobox) react to correlationCache$/activeTimelineDate$.
  loadCorrelationData(queryParams: any) {
    const payload = this.buildCorrelationPayload(queryParams);
    this.getAggregatedCorrelationData(payload).subscribe((response: any) => {
      const timelineDates = this.getTimelineDates(response, payload);
      this.correlationCache$.next({ response, payload, timelineDates });
      this.activeTimelineDate$.next(timelineDates.length ? timelineDates[timelineDates.length - 1] : null);
    }, () => {
      this.correlationCache$.next({ response: null, payload, timelineDates: [] });
      this.activeTimelineDate$.next(null);
    });
  }

  // Manual slider drag / play-bar tick — pure client-side, reuses whatever is already cached.
  setActiveTimelineDate(dateKey: string) {
    if (dateKey === this.activeTimelineDate$.value) {
      return;
    }
    this.activeTimelineDate$.next(dateKey);
  }

  resetTimeline() {
    this.correlationCache$.next(null);
    this.activeTimelineDate$.next(null);
  }

  // Timeline frames are the union of dates found in the wards' own correlationData. At DISTRICT
  // level the self (district-aggregate) row is excluded — its date set has been observed to drift
  // slightly from the real per-ward dates — matching the same exclusion getWardRowsForDate uses.
  getTimelineDates(response: any, payload: any): string[] {
    if (isNullOrUndefined(response) || typeof response !== 'object' || Array.isArray(response)) {
      return [];
    }
    const isWardLevel = payload['pType'] === payload['cType'];
    const dateSet: any = {};
    Object.keys(response).forEach(uuid => {
      if (!isWardLevel && uuid === payload['pUUID']) {
        return;
      }
      const correlationData = response[uuid] && response[uuid]['correlationData'];
      if (correlationData) {
        Object.keys(correlationData).forEach(d => dateSet[d] = true);
      }
    });
    return Object.keys(dateSet).sort();
  }

  getMonthLabel(dateKey: string): string {
    if (isNullOrUndefined(dateKey) || dateKey.length !== 8) {
      return '';
    }
    const month = Number(dateKey.substr(4, 2)) - 1;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month] || '';
  }

  // Ward-wise table rows for a single selected timeline frame. A ward missing data for this
  // exact date is skipped rather than breaking the animation (real API data confirmed this happens).
  getWardRowsForDate(response: any, payload: any, dateKey: string) {
    if (isNullOrUndefined(response) || typeof response !== 'object' || Array.isArray(response) || isNullOrUndefined(dateKey)) {
      return [];
    }
    const rows = Object.keys(response).map(uuid => {
      const location = response[uuid];
      const entry = location['correlationData'] ? location['correlationData'][dateKey] : null;
      if (isNullOrUndefined(entry)) {
        return null;
      }
      return {
        lname: location['lname'],
        luuid: location['luuid'],
        meanLST: entry['meanLST'],
        maxLST: entry['maxLST'],
        uhiClass: !isNullOrUndefined(entry['uhiClass']) && entry['uhiClass'] !== 'null' ? entry['uhiClass'] : 'N/A',
        meanNDVI: entry['meanNDVI'],
        ndbiPercentage: !isNullOrUndefined(entry['ndbiPercentage']) ? Math.round(entry['ndbiPercentage'] * 100) / 100 : null
      };
    }).filter((row: any) => !isNullOrUndefined(row));

    // At DISTRICT level the API also returns the district's own aggregate row (keyed by pUUID) —
    // exclude it from the ward list. At WARD level (pType === cType) that same row IS the answer.
    if (payload['pType'] !== payload['cType']) {
      return rows.filter((row: any) => row.luuid !== payload['pUUID']);
    }
    return rows;
  }

  // The self-row (keyed by pUUID) that getWardRowsForDate excludes from the ward list is exactly
  // the currently-selected location's own aggregate — reused here for the KPI snapshot cards.
  getSelfSnapshotForDate(response: any, pUUID: string, dateKey: string) {
    if (isNullOrUndefined(response) || typeof response !== 'object' || Array.isArray(response) || isNullOrUndefined(dateKey)) {
      return null;
    }
    const location = response[pUUID];
    if (isNullOrUndefined(location) || isNullOrUndefined(location['correlationData'])) {
      return null;
    }
    // The district/ward self-row's own date set has been observed to be sparser than the wards'
    // (e.g. missing the very latest ward date) — rather than blanking the KPI cards on that frame,
    // fall back to whichever available self-row date is closest to the one actually selected.
    const availableDate = this.getClosestAvailableDate(Object.keys(location['correlationData']), dateKey);
    if (isNullOrUndefined(availableDate)) {
      return null;
    }
    const entry = location['correlationData'][availableDate];
    return {
      lname: location['lname'],
      dateKey: availableDate,
      meanLST: entry['meanLST'],
      maxLST: entry['maxLST'],
      meanNDVI: entry['meanNDVI'],
      ndbiPercentage: entry['ndbiPercentage']
    };
  }

  // Not private: reused by getUtilitiesMapForClosestDate below for the same sparse-date-set problem.
  getClosestAvailableDate(availableDates: string[], targetDateKey: string): string {
    if (!availableDates || !availableDates.length) {
      return null;
    }
    if (availableDates.indexOf(targetDateKey) !== -1) {
      return targetDateKey;
    }
    const toDay = (key: string) => Date.UTC(Number(key.substr(0, 4)), Number(key.substr(4, 2)) - 1, Number(key.substr(6, 2)));
    const target = toDay(targetDateKey);
    return availableDates.reduce((closest, candidate) => {
      return Math.abs(toDay(candidate) - target) < Math.abs(toDay(closest) - target) ? candidate : closest;
    }, availableDates[0]);
  }

  formatSnapshotDate(dateKey: string) {
    if (isNullOrUndefined(dateKey) || dateKey.length !== 8) {
      return '';
    }
    const year = Number(dateKey.substr(0, 4));
    const month = Number(dateKey.substr(4, 2)) - 1;
    const day = Number(dateKey.substr(6, 2));
    return this.datePipe.transform(new Date(year, month, day), 'd MMM y');
  }

  // ---- Land Use / Land Cover (category distribution %) ----

  getAggregatedLULCData(postData: any) {
    return this.http.post<any>(this.urlConst + 'correlation/getAggregatedLULCData', postData);
  }

  buildLulcPayload(queryParams: any) {
    const isWardLevel = queryParams['locType'] === 'WARD';
    const year = this.datePipe.transform(new Date(), 'yyyy');
    return {
      pEUUID: GURUGRAM_DISTRICT_UUID,
      pUUID: isWardLevel ? queryParams['pUUID'] : GURUGRAM_DISTRICT_UUID,
      pType: isWardLevel ? 'WARD' : 'DISTRICT',
      cType: 'WARD',
      startTs: year,
      endTs: year,
      format: 'YEARLY',
      source: 'LULC'
    };
  }

  // The API already returns a pre-aggregated self-row keyed by pUUID — at DISTRICT level this is
  // the district-wide aggregate across all wards, at WARD level it's that ward's own distribution.
  // Same lookup works for both, so no manual re-aggregation across ward records is needed.
  getLulcCategoryData(response: any, pUUID: string) {
    if (isNullOrUndefined(response) || typeof response !== 'object' || Array.isArray(response)) {
      return null;
    }
    const location = response[pUUID];
    if (isNullOrUndefined(location) || isNullOrUndefined(location['lulcData'])) {
      return null;
    }
    return location['lulcData'];
  }

  // ---- Heat Exposure (day/night air temp, apparent/wet-bulb/heat index, AUHI/SUHI — monthly) ----
  // Fetched once per location context (like utilitiesCache$ below), then re-derived per play-bar
  // tick purely from this cache + the shared activeTimelineDate$ (the DAILY correlation timeline) —
  // no additional API calls on tick, same pattern applyUtilitiesFrame/utilitiesCache$ already use.
  heatExposureCache$: BehaviorSubject<{ response: any, payload: any }> = new BehaviorSubject(null);

  getUhiKpisData(postData: any) {
    return this.http.post<any>(this.urlConst + 'correlation/getUhiKpisData', postData);
  }

  buildHeatExposurePayload(queryParams: any) {
    const isWardLevel = queryParams['locType'] === 'WARD';
    const year = this.datePipe.transform(new Date(), 'yyyy');
    return {
      pEUUID: GURUGRAM_DISTRICT_UUID,
      pUUID: isWardLevel ? queryParams['pUUID'] : GURUGRAM_DISTRICT_UUID,
      pType: isWardLevel ? 'WARD' : 'DISTRICT',
      cType: isWardLevel ? 'WARD' : 'DISTRICT',
      startTs: `${year}03`,
      endTs: `${year}05`,
      format: 'MONTHLY',
      source: 'UHI_KPIS'
    };
  }

  // Fetches the Heat Exposure API once for the given location context and caches it — same
  // fetch-once-per-location pattern as loadUtilitiesData. Never called on a plain timeline tick.
  loadHeatExposureData(queryParams: any) {
    const payload = this.buildHeatExposurePayload(queryParams);
    this.getUhiKpisData(payload).subscribe((response: any) => {
      this.heatExposureCache$.next({ response, payload });
    }, () => {
      this.heatExposureCache$.next({ response: null, payload });
    });
  }

  // kpisMap is keyed by YYYYMM (monthly) while the shared play-bar timeline (activeTimelineDate$)
  // selects a YYYYMMDD (daily) frame from the DAILY correlation data — so the daily key's own
  // month is looked up first, falling back to the nearest available month (same
  // getClosestAvailableDate idea as the daily KPI cards, just at month granularity) for a range
  // that doesn't yet cover the selected date.
  getHeatExposureSnapshotForDate(response: any, pUUID: string, dateKey: string) {
    if (isNullOrUndefined(response) || typeof response !== 'object' || Array.isArray(response) || isNullOrUndefined(dateKey)) {
      return null;
    }
    const location = response[pUUID];
    if (isNullOrUndefined(location) || isNullOrUndefined(location['kpisMap'])) {
      return null;
    }
    const monthKeys = Object.keys(location['kpisMap']);
    if (!monthKeys.length) {
      return null;
    }
    const targetMonth = dateKey.length >= 6 ? dateKey.substr(0, 6) : dateKey;
    const availableMonth = monthKeys.indexOf(targetMonth) !== -1 ? targetMonth : this.getClosestMonth(monthKeys, targetMonth);
    return { ...location['kpisMap'][availableMonth], monthKey: availableMonth };
  }

  // Same nearest-match idea as getClosestAvailableDate, but comparing YYYYMM keys.
  private getClosestMonth(availableMonths: string[], targetMonth: string): string {
    const toMonthIndex = (key: string) => Number(key.substr(0, 4)) * 12 + (Number(key.substr(4, 2)) - 1);
    const target = toMonthIndex(targetMonth);
    return availableMonths.reduce((closest, candidate) => {
      return Math.abs(toMonthIndex(candidate) - target) < Math.abs(toMonthIndex(closest) - target) ? candidate : closest;
    }, availableMonths[0]);
  }

  formatMonthLabel(monthKey: string): string {
    if (isNullOrUndefined(monthKey) || monthKey.length !== 6) {
      return '';
    }
    const year = Number(monthKey.substr(0, 4));
    const month = Number(monthKey.substr(4, 2)) - 1;
    return this.datePipe.transform(new Date(year, month, 1), 'MMM y');
  }

  // ---- Utilities exposure (count of buildings/schools/etc. bucketed by LST range) ----

  getUtilitiesData(postData: any) {
    return this.http.post<any>(this.urlConst + 'correlation/getUtilitiesData', postData);
  }

  buildUtilitiesPayload(queryParams: any) {
    const isWardLevel = queryParams['locType'] === 'WARD';
    return {
      pEUUID: GURUGRAM_DISTRICT_UUID,
      pUUID: isWardLevel ? queryParams['pUUID'] : GURUGRAM_DISTRICT_UUID,
      pType: isWardLevel ? 'WARD' : 'DISTRICT',
      cType: isWardLevel ? 'WARD' : 'DISTRICT',
      "startTs": "20260322",
      "endTs": "20260525",
      format: 'DAILY',
      source: 'CORRELATION_UTILITIES'
    };
  }

  // Bumped per loadUtilitiesData call so a slower response for a previous location (e.g. a quick
  // ward -> Gurgaon switch) can't overwrite the cache after the latest one has landed.
  private utilitiesRequestToken = 0;

  // Fetches the utilities API once for the given location context and caches it — same
  // fetch-once-per-location pattern as loadCorrelationData. Never called on a plain timeline tick.
  loadUtilitiesData(queryParams: any) {
    const payload = this.buildUtilitiesPayload(queryParams);
    const requestToken = ++this.utilitiesRequestToken;
    this.getUtilitiesData(payload).subscribe((response: any) => {
      if (requestToken !== this.utilitiesRequestToken) { return; }
      this.utilitiesCache$.next({ response, payload });
    }, () => {
      if (requestToken !== this.utilitiesRequestToken) { return; }
      this.utilitiesCache$.next({ response: null, payload });
    });
  }

  // utilitiesMap's own date keys are sparser than the correlation timeline's (a handful of
  // snapshot dates vs a daily series) — reuse getClosestAvailableDate so the chart still shows
  // the nearest available snapshot as the play bar/slider moves, instead of going blank on most frames.
  getUtilitiesMapForClosestDate(response: any, pUUID: string, dateKey: string): any {
    if (isNullOrUndefined(response) || typeof response !== 'object' || Array.isArray(response) || isNullOrUndefined(dateKey)) {
      return null;
    }
    const location = response[pUUID];
    if (isNullOrUndefined(location) || isNullOrUndefined(location['utilitiesMap'])) {
      return null;
    }
    const availableDate = this.getClosestAvailableDate(Object.keys(location['utilitiesMap']), dateKey);
    if (isNullOrUndefined(availableDate)) {
      return null;
    }
    return location['utilitiesMap'][availableDate];
  }
}
